import React, { useState, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Upload, FileText, X, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PdfUpload = ({ value, onChange, label, testId }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('PDF too large. Max 25MB allowed.');
      return;
    }
    if (file.type !== 'application/pdf') {
      toast.error('Only PDF files allowed.');
      return;
    }
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const { data } = await axios.post(`${API_URL}/api/upload-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      onChange(data.url);
      toast.success('PDF uploaded successfully!');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Upload failed';
      toast.error(typeof msg === 'string' ? msg : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={testId || 'pdf-upload'}>
      {label && (
        <label className="block text-sm font-body font-medium text-charcoal-700">{label}</label>
      )}
      <p className="text-xs text-charcoal-500 italic">Max 25MB · PDF only</p>
      <div className="flex gap-3">
        <div className="flex-1">
          <input
            type="text"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="PDF URL or upload below"
            className="w-full px-4 py-3 rounded-lg border border-charcoal-200 focus:outline-none focus:ring-2 focus:ring-saffron-500 font-body text-sm"
          />
        </div>
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex items-center gap-2 bg-saffron-600 text-white rounded-lg px-4 py-3 hover:bg-saffron-500 transition-colors disabled:opacity-50 text-sm font-medium whitespace-nowrap"
          data-testid={`${testId}-upload-button`}
        >
          {uploading ? <Loader2 size={18} className="animate-spin" /> : <Upload size={18} />}
          {uploading ? 'Uploading...' : 'Upload PDF'}
        </button>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/pdf"
        onChange={(e) => handleUpload(e.target.files?.[0])}
        className="hidden"
      />
      {value && (
        <div className="mt-2 inline-flex items-center gap-2 bg-cream border border-charcoal-100 rounded-lg px-3 py-2 text-sm">
          <FileText size={16} className="text-saffron-600" />
          <a href={value} target="_blank" rel="noopener noreferrer" className="text-charcoal-700 hover:text-saffron-600 truncate max-w-xs">
            View uploaded PDF
          </a>
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-red-500 hover:text-red-700"
            aria-label="Remove PDF"
          >
            <X size={14} />
          </button>
        </div>
      )}
    </div>
  );
};
